/**
 * cache/FusionCache.ts — intelligent per-domain TTL cache.
 *
 * Fresh entries are served without touching a provider. Stale-but-present
 * entries are still returned as a fallback when every provider fails, so the
 * user never sees a broken card. TTLs are per-domain and future-configurable.
 */

import type { FusionDomain } from '../models';

/** Suggested defaults from the Fusion spec (ms). */
export const DEFAULT_CACHE_TTL_MS: Record<FusionDomain, number> = (() => {
  const MIN = 60_000;
  const HOUR = 60 * MIN;
  const table: Partial<Record<FusionDomain, number>> = {
    weather: 15 * MIN,
    currency: 15 * MIN,
    currencyExchange: 15 * MIN,
    places: 24 * HOUR,
    maps: 24 * HOUR,
    restaurant: 24 * HOUR,
    cafe: 24 * HOUR,
    coworking: 24 * HOUR,
    attraction: 24 * HOUR,
    atm: 24 * HOUR,
    bank: 24 * HOUR,
    hospital: 24 * HOUR,
    pharmacy: 24 * HOUR,
    embassy: 24 * HOUR,
    airport: 24 * HOUR,
    grocery: 24 * HOUR,
    laundry: 24 * HOUR,
    gym: 24 * HOUR,
    simProvider: 24 * HOUR,
    utility: 24 * HOUR,
    emergencyContact: 24 * HOUR,
    housing: 6 * HOUR,
    hotel: 6 * HOUR,
    hostel: 6 * HOUR,
    monthlyRental: 6 * HOUR,
    events: 30 * MIN,
    internetSpeed: 7 * 24 * HOUR,
    airQuality: 15 * MIN,
    safety: 12 * HOUR,
    travelAdvisory: 12 * HOUR,
    communityReviews: 5 * MIN,
    costOfLiving: 7 * 24 * HOUR,
    visa: 7 * 24 * HOUR,
    publicTransit: 24 * HOUR,
    transportation: 24 * HOUR,
  };
  // Any domain not explicitly listed defaults to 1 hour.
  const full = {} as Record<FusionDomain, number>;
  for (const key of Object.keys(table) as FusionDomain[]) full[key] = table[key]!;
  return new Proxy(full, {
    get: (target, prop: string) => (prop in target ? target[prop as FusionDomain] : HOUR),
  });
})();

interface CacheEntry<T> {
  value: T;
  storedAt: number;
  ttlMs: number;
}

export interface CacheLookup<T> {
  value: T;
  storedAt: number;
  ageMs: number;
  stale: boolean;
}

export class FusionCache {
  private store = new Map<string, CacheEntry<unknown>>();

  constructor(private ttlByDomain: Record<FusionDomain, number> = DEFAULT_CACHE_TTL_MS) {}

  /** Override the TTL for a domain at runtime (future configuration hook). */
  setTtl(domain: FusionDomain, ttlMs: number): void {
    this.ttlByDomain[domain] = ttlMs;
  }

  getTtl(domain: FusionDomain): number {
    return this.ttlByDomain[domain];
  }

  set<T>(key: string, domain: FusionDomain, value: T, now: number): void {
    this.store.set(key, { value, storedAt: now, ttlMs: this.ttlByDomain[domain] });
  }

  /** Look up an entry regardless of freshness; caller decides via `stale`. */
  peek<T>(key: string, now: number): CacheLookup<T> | null {
    const entry = this.store.get(key) as CacheEntry<T> | undefined;
    if (!entry) return null;
    const ageMs = now - entry.storedAt;
    return { value: entry.value, storedAt: entry.storedAt, ageMs, stale: ageMs > entry.ttlMs };
  }

  /** Fresh entry only (miss if stale). */
  getFresh<T>(key: string, now: number): T | null {
    const found = this.peek<T>(key, now);
    return found && !found.stale ? found.value : null;
  }

  delete(key: string): void {
    this.store.delete(key);
  }

  /**
   * Scoped invalidation (Sprint 1.88 rule: "changing Bangkok must not
   * invalidate Tokyo"). Drops ONLY entries keyed to the given locationId —
   * cache keys are `${domain}|${locationId}|${limit}|${paramSig}`.
   * Returns the number of entries removed.
   */
  invalidateLocation(locationId: string): number {
    let removed = 0;
    for (const key of Array.from(this.store.keys())) {
      const parts = key.split('|');
      if (parts[1] === locationId) {
        this.store.delete(key);
        removed += 1;
      }
    }
    return removed;
  }

  clear(): void {
    this.store.clear();
  }

  get size(): number {
    return this.store.size;
  }
}
